import i18next from "i18next";
import { useContext } from "react";
import { useMutation, useQueryClient } from "react-query";

import { useClient } from "utils/client";
import { ControlContext } from "context/Controls";
import { TrackingContext } from "context/Tracking";

import { getAIRiskApi, isTimeoutError } from "../api";
import { AIAnalyzeMode, AIRiskSession } from "../types";
import { AI_RISK_SESSION_QUERY_KEY } from "./sessionCache";

interface AnalyzeProjectVariables {
  additional_context: string;
  mode: AIAnalyzeMode;
}

interface AnalyzeProjectContext {
  startedAt: number;
}

// Stage 1: synchronous POST /analyze (client timeout 300 s, see api.ts).
// The server gathers the project baseline itself; we only send the
// user-typed additional context + the current UI language.
export const useAnalyzeProject = (projectId: string) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { track } = useContext(TrackingContext);
  const { displayNotification } = useContext(ControlContext);

  return useMutation<
    AIRiskSession,
    Error,
    AnalyzeProjectVariables,
    AnalyzeProjectContext
  >(
    ({ additional_context, mode }) =>
      getAIRiskApi(client).analyze(projectId, {
        language: i18next.language,
        additional_context,
        mode,
      }),
    {
      onMutate: () => ({ startedAt: Date.now() }),
      onSuccess: (session, { mode }, context) => {
        // The analyze response IS the persisted session — no refetch needed.
        queryClient.setQueryData<AIRiskSession | null>(
          [AI_RISK_SESSION_QUERY_KEY, projectId],
          session
        );

        track("ai risk: analysis completed", {
          groups: session.groups.length,
          hazards: session.hazards.length,
          mode,
          duration_ms: Date.now() - (context?.startedAt ?? Date.now()),
        });
      },
      onError: (error) => {
        track("ai risk: analysis failed", { message: error.message });

        displayNotification!(
          i18next.t(
            isTimeoutError(error)
              ? "risks.ai.errors.timeout"
              : "risks.ai.errors.analysisFailed"
          ),
          error.message,
          "error"
        );
      },
    }
  );
};
